import { Grid, Typography } from '@mui/material';
import { isEmpty, toNumber, toString } from 'lodash';
import { Controller } from 'react-hook-form';
import { InfoBox } from 'src/components/infoBox/InfoBox';
import { Progress } from 'src/components/progress';
import { WarningBox } from 'src/components/warningBox/warningBox';
import { InputWidget, SelectDropdownWidget } from 'src/components/widget';
import { IViewLocationGroupList } from 'src/interfaces';
import { lang } from 'src/utils';

import { SimpleDialog } from '../simpleDialog';
import { useAssignLocationsModal } from './AssignLocationsModal.hook';
import {
  AssignLocationsModalProps,
  FormProps,
} from './AssignLocationsModal.interface';

export const AssignLocationsModal = (props: AssignLocationsModalProps) => {
  const {
    formMethod,
    fieldArray,
    selectedLocation,
    isLoadingViewLocationGroups,
  } = useAssignLocationsModal(props.locationCode ?? '');

  const displayOptions = (locationGroups: IViewLocationGroupList[]) =>
    locationGroups?.map((locationGroup) => ({
      label: `${locationGroup.name} - ${locationGroup.screenConfigurationLabel}`,
      value: toString(locationGroup.id),
    }));

  const onValidation = () => {
    if (!isAllSelectedLocationGroupsFilled) {
      return lang.allChannelsMustBeAssignedToSave;
    }

    return undefined;
  };

  const onSubmit = (data: FormProps) => {
    const locationGroups = data.channels.reduce((acc, channel) => {
      if (channel.selectedLocationGroupId) {
        acc.push(
          channel.locationGroups.find(
            (item) => item.id === channel.selectedLocationGroupId,
          ) as IViewLocationGroupList,
        );
      }

      return acc;
    }, [] as IViewLocationGroupList[]);

    props.onClickConfirm(locationGroups);
  };

  const isAllSelectedLocationGroupsFilled = () => {
    const channels = formMethod.getValues('channels');

    return channels.every((channel) =>
      Boolean(channel.selectedLocationGroupId),
    );
  };

  if (isLoadingViewLocationGroups) return <Progress />;

  return (
    <SimpleDialog
      {...props}
      cancelText={lang.cancel}
      confirmText={lang.confirm}
      modalSize="md"
      title={
        <Typography
          sx={{
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            maxWidth: '100%',
            padding: '16px 24px',
          }}>
          {`Set ${selectedLocation?.name} to a new location group`}
        </Typography>
      }
      subTitle={
        <InfoBox visible>{lang.allChannelsMustBeAssignedToSave}</InfoBox>
      }
      disabled={
        isEmpty(fieldArray.fields) || !isAllSelectedLocationGroupsFilled()
      }
      onClickCancel={props.onClose}
      onClickConfirm={formMethod.handleSubmit(onSubmit)}>
      <Grid container spacing={2}>
        {!props.isLocationGroupsLoading && (
          <>
            <Grid item xs={6}>
              <Typography>{lang.channel}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography>{lang.locationGroup}</Typography>
            </Grid>
          </>
        )}

        {props.isLocationGroupsLoading ? (
          <Grid container sx={{ margin: 10 }}>
            <Progress />
          </Grid>
        ) : (
          fieldArray.fields?.map((channel, index) => {
            return (
              <Controller
                key={channel.uid}
                rules={{
                  validate: onValidation,
                }}
                name={`channels.${index}`}
                control={formMethod.control}
                render={({ field }) => {
                  const errorMessage =
                    formMethod.formState.errors.channels?.[index]?.message;

                  return (
                    <>
                      <Grid item xs={6}>
                        <InputWidget
                          label=" "
                          onChange={() => {}}
                          disabled
                          value={field.value.name}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        {isEmpty(channel.locationGroups) ? (
                          <WarningBox
                            sx={{
                              paddingTop: '13px',
                              marginTop: '5px',
                              height: '84%',
                              width: '100%',
                            }}>
                            {lang.noLocationGroup}
                          </WarningBox>
                        ) : (
                          <SelectDropdownWidget
                            {...field}
                            onChange={async (value) => {
                              field.onChange({
                                ...field.value,
                                selectedLocationGroupId:
                                  toNumber(value) || undefined,
                              });

                              await formMethod.trigger();
                            }}
                            value={field.value.selectedLocationGroupId}
                            selectOptions={displayOptions(
                              field.value.locationGroups,
                            )}
                            error={errorMessage}
                            hasError={!!errorMessage}
                          />
                        )}
                      </Grid>
                    </>
                  );
                }}
              />
            );
          })
        )}
      </Grid>
    </SimpleDialog>
  );
};
